import React, { useState } from 'react';
import { Star, Send, X, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

/**
 * Inline feedback widget shown right after a successful generation.
 *
 * Props:
 *   mediaType: 'ad' | 'video'
 *   mediaId:   id of the ad / video job
 *   onClose?:  optional callback when dismissed
 */
const FeedbackWidget = ({ mediaType, mediaId, onClose }) => {
  const [hover, setHover] = useState(0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [allowShowcase, setAllowShowcase] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (submitted) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2"
           data-testid="feedback-thanks">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <div className="font-semibold">Thanks for the feedback!</div>
          <div className="text-emerald-800 text-xs mt-0.5">
            {allowShowcase ? "We may showcase your great work — anonymously by first name only." : "Filed away for our team only."}
          </div>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await api.post('/feedback/submit', { mediaType, mediaId, rating, comment, allowShowcase });
      setSubmitted(true);
    } catch (e) {
      // silent failure — feedback is non-critical
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white p-4 relative" data-testid="feedback-widget">
      {onClose && (
        <button onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
                data-testid="feedback-close">
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="text-sm font-semibold text-slate-900">How was your {mediaType}?</div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-3">Helps us improve the AI behind the scenes.</div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1,2,3,4,5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            data-testid={`feedback-star-${n}`}
            className="p-0.5"
            aria-label={`Rate ${n} stars`}
          >
            <Star
              className={`w-7 h-7 transition ${(hover || rating) >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
            />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <div className="mt-3 space-y-2">
          <Textarea
            placeholder={rating >= 4 ? "What did you love? (optional)" : "What could we improve? (optional)"}
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="text-sm min-h-[70px]"
            data-testid="feedback-comment"
          />
          {rating >= 4 && (
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allowShowcase}
                onChange={e => setAllowShowcase(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                data-testid="feedback-showcase-optin"
              />
              <span>Let MBAP feature this in our marketing showcase (first name + business type only — no PII).</span>
            </label>
          )}
          <Button
            onClick={submit}
            disabled={busy}
            size="sm"
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="feedback-submit-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {busy ? 'Sending…' : 'Send feedback'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
